import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  FaWallet,
  FaChartPie,
  FaCalendarAlt,
  FaCalendarWeek,
  FaHistory,
  FaTags,
  FaSyncAlt,
  FaMoneyBillWave,
  FaCreditCard,
  FaReceipt,
  FaExchangeAlt,
  FaSearch,
  FaPlus,
  FaPrint,
  FaTimes,
  FaEye,
  FaPaperclip,
  FaEdit,
  FaTrash,
  FaListUl,
  FaArrowUp,
  FaArrowDown,
  FaDollarSign,
  FaBuilding,
  FaChartLine,
  FaFileInvoice,
  FaPercent,
  FaFilter,
  FaSlidersH,
  FaCheckCircle,
  FaFileAlt,
  FaListAlt,
  FaChartBar,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import ExpenseForm from "../../Components/forms/ExpenseForm";
import useAuth from "../../useAuth";
import { server } from "../../server";

const GRADIENT_COLORS = [
  ["#EF4444", "#FCA5A5"],
  ["#F97316", "#FDBA74"],
  ["#F59E0B", "#FDE68A"],
  ["#10B981", "#6EE7B7"],
  ["#06B6D4", "#67E8F9"],
  ["#3B82F6", "#93C5FD"],
  ["#6366F1", "#A5B4FC"],
  ["#8B5CF6", "#C4B5FD"],
  ["#EC4899", "#F9A8D4"],
];

const cardVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut",
    },
  }),
};

const StatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  color,
  gradient,
  trend,
  index,
}) => (
  <motion.div
    custom={index}
    initial="hidden"
    animate="visible"
    variants={cardVariants}
    className="relative bg-white dark:bg-slate-900 rounded-xl p-3.5 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group"
  >
    <div
      className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient} rounded-t-xl`}
    />
    <div className="absolute -top-12 -right-12 w-28 h-28 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-br from-white to-transparent" />

    <div className="flex items-center justify-between mb-2">
      <div
        className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center text-white shadow-md transform group-hover:scale-105 transition-transform duration-300`}
      >
        <Icon className="text-sm" />
      </div>
      {trend !== undefined && trend !== null && (
        <div
          className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${
            trend >= 0
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
              : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
          }`}
        >
          {trend >= 0 ? <FaArrowUp size={8} /> : <FaArrowDown size={8} />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>

    <div className="space-y-0.5">
      <div className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
        {value}
      </div>
      <div className="text-xs font-bold text-slate-600 dark:text-slate-300">
        {title}
      </div>
      {subtitle && (
        <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500 truncate">
          {subtitle}
        </div>
      )}
    </div>
  </motion.div>
);

export default function ExpenseDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ fromDate: "", toDate: "" });
  const [activePreset, setActivePreset] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  // Modals state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      let params = {};
      if (dateRange.fromDate) params.fromDate = dateRange.fromDate;
      if (dateRange.toDate) params.toDate = dateRange.toDate;

      let res;
      try {
        res = await axios.get(`${server}/expense-dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
      } catch (err) {
        res = await axios.get(`${server}/expenses/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
      }

      setDashboardData(res.data.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load expense dashboard analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchDashboardData();
    // eslint-disable-next-line
  }, [token, dateRange.fromDate, dateRange.toDate]);

  const applyPreset = (preset) => {
    setActivePreset(preset);
    const today = new Date();
    let from = "";
    let to = today.toISOString().split("T")[0];

    if (preset === "TODAY") {
      from = to;
    } else if (preset === "THIS_WEEK") {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(today.setDate(diff));
      from = monday.toISOString().split("T")[0];
    } else if (preset === "THIS_MONTH") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      from = firstDay.toISOString().split("T")[0];
    } else if (preset === "THIS_YEAR") {
      const firstDay = new Date(today.getFullYear(), 0, 1);
      from = firstDay.toISOString().split("T")[0];
    } else {
      from = "";
      to = "";
    }

    setDateRange({ fromDate: from, toDate: to });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleDeleteExpense = async (id, title) => {
    if (
      window.confirm(
        `Are you sure you want to delete expense record '${title}'?`
      )
    ) {
      try {
        await axios.delete(`${server}/expenses/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Expense transaction deleted successfully");
        fetchDashboardData();
      } catch (error) {
        toast.error("Error deleting expense transaction");
      }
    }
  };

  const openCreateModal = () => {
    setSelectedExpense(null);
    setIsFormModalOpen(true);
  };

  const openEditModal = (tx) => {
    setSelectedExpense(tx);
    setIsFormModalOpen(true);
  };

  const openViewModal = (tx) => {
    setViewingExpense(tx);
    setIsViewModalOpen(true);
  };

  const handlePrintReport = () => {
    window.print();
  };

  // Filter & sort transactions
  const rawTransactions = dashboardData?.recentTransactions || [];
  const filteredTransactions = rawTransactions
    .filter((tx) => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !term ||
        tx.title?.toLowerCase().includes(term) ||
        (typeof tx.category === "object"
          ? tx.category?.name?.toLowerCase().includes(term)
          : tx.category?.toLowerCase().includes(term)) ||
        tx.paymentMethod?.toLowerCase().includes(term) ||
        tx.referenceOrReceiptNo?.toLowerCase().includes(term);

      const catName =
        typeof tx.category === "object" ? tx.category?.name : tx.category;
      const matchesCategory =
        categoryFilter === "ALL" ||
        (catName && catName.toLowerCase() === categoryFilter.toLowerCase());

      const matchesPayment =
        paymentFilter === "ALL" ||
        (tx.paymentMethod &&
          tx.paymentMethod.toLowerCase() === paymentFilter.toLowerCase());

      return matchesSearch && matchesCategory && matchesPayment;
    })
    .sort((a, b) => {
      if (sortConfig.key === "date") {
        return sortConfig.direction === "asc"
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      }
      if (sortConfig.key === "amount") {
        return sortConfig.direction === "asc"
          ? a.amount - b.amount
          : b.amount - a.amount;
      }
      return 0;
    });

  // Financial ratios
  const totalRev = dashboardData?.monthFeeCollected || 0;
  const totalExp = dashboardData?.totalExpenses || 0;
  const expenseRatio =
    totalRev > 0
      ? Math.min(100, Math.round((totalExp / totalRev) * 100))
      : totalExp > 0
      ? 100
      : 0;
  const netMarginPercent =
    totalRev > 0
      ? Math.round(((totalRev - totalExp) / totalRev) * 100)
      : totalExp > 0
      ? 0
      : 100;

  return (
    <div className="p-3 md:p-4 max-w-[1440px] mx-auto min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      <div className="space-y-3.5">
        {/* ════════════════ HEADER SECTION ════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3.5 md:p-4 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-md"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-md shadow-red-500/20">
              <FaChartPie className="text-lg" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-400 bg-clip-text text-transparent leading-tight">
                Financial Analytics
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                Real-time expense tracking, category breakdown, and institutional insights
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800/50 p-1 rounded-lg border border-slate-200/50 dark:border-slate-700/50">
              <div className="flex flex-col px-1.5">
                <span className="text-[9px] font-bold text-slate-400 uppercase">
                  From
                </span>
                <input
                  type="date"
                  value={dateRange.fromDate}
                  onChange={(e) => {
                    setActivePreset("CUSTOM");
                    setDateRange({ ...dateRange, fromDate: e.target.value });
                  }}
                  className="bg-transparent border-none text-xs font-semibold text-slate-900 dark:text-white focus:ring-0 outline-none p-0"
                />
              </div>
              <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
              <div className="flex flex-col px-1.5">
                <span className="text-[9px] font-bold text-slate-400 uppercase">
                  To
                </span>
                <input
                  type="date"
                  value={dateRange.toDate}
                  onChange={(e) => {
                    setActivePreset("CUSTOM");
                    setDateRange({ ...dateRange, toDate: e.target.value });
                  }}
                  className="bg-transparent border-none text-xs font-semibold text-slate-900 dark:text-white focus:ring-0 outline-none p-0"
                />
              </div>
            </div>

            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg transition border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <FaSyncAlt className={loading ? "animate-spin text-xs" : "text-xs"} />
              Refresh
            </button>

            <button
              onClick={() => navigate("/expenses")}
              className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white text-xs font-bold rounded-lg shadow-sm shadow-rose-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <FaListAlt className="text-xs" /> Expenditure Register
            </button>

            <button
              onClick={() => navigate("/my/fees/dashboard")}
              className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm shadow-emerald-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <FaWallet className="text-xs" /> Fee Dashboard
            </button>

            <button
              onClick={handlePrintReport}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg transition border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 cursor-pointer"
            >
              <FaPrint className="text-slate-500 text-xs" /> Print
            </button>
          </div>
        </motion.div>

        {/* ════════════════ PRESET TABS ════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, duration: 0.3 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-1.5 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-md flex items-center justify-between gap-2 overflow-x-auto"
        >
          <div className="flex items-center gap-1">
            {[
              { label: "All Time", value: "ALL" },
              { label: "Today", value: "TODAY" },
              { label: "This Week", value: "THIS_WEEK" },
              { label: "This Month", value: "THIS_MONTH" },
              { label: "This Year", value: "THIS_YEAR" },
            ].map((preset) => (
              <motion.button
                key={preset.value}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => applyPreset(preset.value)}
                className={`px-3 py-1 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  activePreset === preset.value
                    ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm shadow-red-500/20"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                {preset.label}
              </motion.button>
            ))}
          </div>

          <button
            onClick={openCreateModal}
            className="px-3 py-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm shadow-emerald-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <FaPlus className="text-xs" /> Add Expense
          </button>
        </motion.div>

        {/* ════════════════ MAIN CONTENT ════════════════ */}
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-md">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-red-200 dark:border-red-900/30 rounded-full animate-spin border-t-red-500" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-4 border-red-300 dark:border-red-800/30 rounded-full animate-ping" />
              </div>
            </div>
            <p className="mt-4 text-slate-500 dark:text-slate-400 text-xs font-semibold animate-pulse">
              Loading financial analytics...
            </p>
          </div>
        ) : !dashboardData ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-md text-slate-500 dark:text-slate-400">
            <FaChartPie className="text-4xl text-slate-300 dark:text-slate-700 mb-3" />
            <p className="text-base font-bold">Failed to load dashboard data</p>
            <p className="text-xs">Please verify your network connection</p>
          </div>
        ) : (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-3.5"
            >
              {/* ════════ TOP KPI STAT CARDS ════════ */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                  index={0}
                  icon={FaWallet}
                  title="Total Expenses"
                  value={formatCurrency(dashboardData.totalExpenses)}
                  subtitle={`${dashboardData.totalExpenseCount || 0} transactions`}
                  color="bg-gradient-to-br from-red-500 to-red-600"
                  gradient="from-red-500 to-red-600"
                  trend={3.2}
                />

                <StatCard
                  index={1}
                  icon={FaCalendarAlt}
                  title="Monthly Spend"
                  value={formatCurrency(dashboardData.monthlyExpenses)}
                  subtitle={`Weekly burn: ${formatCurrency(dashboardData.weeklyExpenses)}`}
                  color="bg-gradient-to-br from-orange-500 to-orange-600"
                  gradient="from-orange-500 to-orange-600"
                  trend={-1.5}
                />

                <StatCard
                  index={2}
                  icon={FaMoneyBillWave}
                  title="Net Surplus"
                  value={formatCurrency(dashboardData.monthNetBalance ?? 0)}
                  subtitle={`Fees: ${formatCurrency(dashboardData.monthFeeCollected)}`}
                  color={`bg-gradient-to-br ${(dashboardData.monthNetBalance ?? 0) >= 0 ? "from-emerald-500 to-emerald-600" : "from-red-500 to-red-600"}`}
                  gradient={
                    (dashboardData.monthNetBalance ?? 0) >= 0
                      ? "from-emerald-500 to-emerald-600"
                      : "from-red-500 to-red-600"
                  }
                  trend={(dashboardData.monthNetBalance ?? 0) >= 0 ? 8.7 : -2.3}
                />

                <StatCard
                  index={3}
                  icon={FaPercent}
                  title="Expense Ratio"
                  value={`${netMarginPercent}%`}
                  subtitle={`${expenseRatio}% burn rate`}
                  color="bg-gradient-to-br from-indigo-500 to-indigo-600"
                  gradient="from-indigo-500 to-indigo-600"
                  trend={0.5}
                />
              </div>

              {/* ════════ SECONDARY METRICS ROW ════════ */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-lg shadow-sm">
                    <FaMoneyBillWave className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Fee Revenue
                    </span>
                    <p className="font-extrabold text-slate-900 dark:text-white text-xs md:text-sm">
                      {formatCurrency(dashboardData.monthFeeCollected)}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg shadow-sm">
                    <FaCalendarWeek className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Weekly Burn
                    </span>
                    <p className="font-extrabold text-slate-900 dark:text-white text-xs md:text-sm">
                      {formatCurrency(dashboardData.weeklyExpenses)}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg shadow-sm">
                    <FaTags className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Active Categories
                    </span>
                    <p className="font-extrabold text-slate-900 dark:text-white text-xs md:text-sm">
                      {dashboardData.categoryBreakdown?.length || 0} Categories
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-sm">
                    <FaCheckCircle className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Audit Status
                    </span>
                    <p className="font-extrabold text-emerald-600 dark:text-emerald-400 text-xs md:text-sm">
                      Verified Entries
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* ════════ CHARTS SECTION 1: DAILY AREA CHART & CATEGORY DONUT ════════ */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
                {/* Daily Expense Area Chart (14 Days) */}
                <motion.div
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="lg:col-span-8 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <FaChartLine className="text-red-500 text-xs" />
                        Daily Expense Trend (Last 14 Days)
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Real-time daily expenditure volume
                      </p>
                    </div>
                  </div>

                  <div className="h-[220px] w-full">
                    {dashboardData.dailyChart && dashboardData.dailyChart.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={dashboardData.dailyChart}
                          margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient
                              id="roseArea"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#EF4444"
                                stopOpacity={0.8}
                              />
                              <stop
                                offset="100%"
                                stopColor="#EF4444"
                                stopOpacity={0.05}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#E2E8F0"
                            className="dark:opacity-10"
                          />
                          <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                            dy={5}
                          />
                          <YAxis
                            tickFormatter={(value) =>
                              `₹${value >= 1000 ? (value / 1000).toFixed(0) + "k" : value}`
                            }
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          />
                          <Tooltip
                            cursor={{ stroke: "#EF4444", strokeWidth: 1, strokeDasharray: "3 3" }}
                            contentStyle={{
                              backgroundColor: "#0F172A",
                              borderRadius: "10px",
                              border: "none",
                              color: "#FFFFFF",
                              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
                              padding: "8px 12px",
                              fontSize: "11px",
                            }}
                            formatter={(value) => [formatCurrency(value), "Expenditure"]}
                          />
                          <Area
                            type="monotone"
                            dataKey="amount"
                            stroke="#EF4444"
                            strokeWidth={2.5}
                            fillOpacity={1}
                            fill="url(#roseArea)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 italic text-xs">
                        No daily trend data available
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Category Donut PieChart */}
                <motion.div
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25, duration: 0.4 }}
                  className="lg:col-span-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <FaTags className="text-red-500 text-xs" />
                        Categories Breakdown
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Expense distribution by category
                      </p>
                    </div>
                  </div>

                  <div className="h-[220px] w-full">
                    {dashboardData.categoryBreakdown &&
                    dashboardData.categoryBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <defs>
                            {dashboardData.categoryBreakdown.map(
                              (entry, index) => (
                                <linearGradient
                                  key={`grad-${index}`}
                                  id={`grad-${index}`}
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="0%"
                                    stopColor={
                                      GRADIENT_COLORS[
                                        index % GRADIENT_COLORS.length
                                      ][0]
                                    }
                                  />
                                  <stop
                                    offset="100%"
                                    stopColor={
                                      GRADIENT_COLORS[
                                        index % GRADIENT_COLORS.length
                                      ][1]
                                    }
                                  />
                                </linearGradient>
                              )
                            )}
                          </defs>
                          <Pie
                            data={dashboardData.categoryBreakdown.map(
                              (item) => ({
                                ...item,
                                value: Number(item.value) || 0,
                              })
                            )}
                            cx="50%"
                            cy="45%"
                            innerRadius={42}
                            outerRadius={68}
                            paddingAngle={3}
                            dataKey="value"
                            stroke="none"
                          >
                            {dashboardData.categoryBreakdown.map(
                              (entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={`url(#grad-${index})`}
                                  className="hover:opacity-80 transition-opacity cursor-pointer"
                                />
                              )
                            )}
                          </Pie>
                          <Tooltip
                            formatter={(value, name, props) => [
                              `${formatCurrency(value)} (${props.payload.percentage || 0}%)`,
                              props.payload.name || "Category",
                            ]}
                            contentStyle={{
                              backgroundColor: "#0F172A",
                              borderRadius: "8px",
                              border: "none",
                              color: "#FFFFFF",
                              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
                              fontSize: "10px",
                              fontWeight: "bold",
                              padding: "6px 10px",
                            }}
                          />
                          <Legend
                            iconType="circle"
                            formatter={(value, entry) => (
                              <span className="text-slate-700 dark:text-slate-300 font-semibold text-[10px]">
                                {value} ({entry.payload?.percentage || 0}%)
                              </span>
                            )}
                            wrapperStyle={{
                              fontSize: "10px",
                              fontWeight: 600,
                              paddingTop: "5px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 italic text-xs">
                        No categories available
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* ════════ CHARTS SECTION 2: 6-MONTH TREND & INSTITUTIONAL INSIGHTS ════════ */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
                {/* 6-Month Expenditure vs Revenue BarChart */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="lg:col-span-8 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <FaChartBar className="text-indigo-500 text-xs" />
                        6-Month Financial Trend (Expenditure vs Revenue)
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Monthly expenditure compared against fee collection revenue
                      </p>
                    </div>
                    <div className="flex items-center gap-2.5 text-[11px]">
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                        <span className="font-semibold text-slate-600 dark:text-slate-400">
                          Expenses
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        <span className="font-semibold text-slate-600 dark:text-slate-400">
                          Revenue
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="h-[210px] w-full">
                    {dashboardData.sixMonthTrend &&
                    dashboardData.sixMonthTrend.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={dashboardData.sixMonthTrend}
                          margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient
                              id="expenseGrad"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#EF4444"
                                stopOpacity={0.85}
                              />
                              <stop
                                offset="100%"
                                stopColor="#EF4444"
                                stopOpacity={0.4}
                              />
                            </linearGradient>
                            <linearGradient
                              id="revenueGrad"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#10B981"
                                stopOpacity={0.85}
                              />
                              <stop
                                offset="100%"
                                stopColor="#10B981"
                                stopOpacity={0.4}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#E2E8F0"
                            className="dark:opacity-10"
                          />
                          <XAxis
                            dataKey="monthName"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                            dy={5}
                          />
                          <YAxis
                            tickFormatter={(value) =>
                              `₹${value >= 1000 ? (value / 1000).toFixed(0) + "k" : value}`
                            }
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          />
                          <Tooltip
                            cursor={{ fill: "rgba(226, 232, 240, 0.2)" }}
                            contentStyle={{
                              backgroundColor: "#0F172A",
                              borderRadius: "10px",
                              border: "none",
                              color: "#FFFFFF",
                              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
                              padding: "8px 12px",
                              fontSize: "11px",
                            }}
                            formatter={(value) => [formatCurrency(value), ""]}
                          />
                          <Bar
                            name="Expenses"
                            dataKey="amount"
                            fill="url(#expenseGrad)"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={30}
                          />
                          <Bar
                            name="Revenue"
                            dataKey="feeCollection"
                            fill="url(#revenueGrad)"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={30}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 italic text-xs">
                        No trend data available
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Institutional Quick Operations Panel */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.4 }}
                  className="lg:col-span-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md flex flex-col justify-between"
                >
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
                      <FaMoneyBillWave className="text-red-500 text-xs" />
                      Institutional Insights
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">
                      Quick management operations
                    </p>

                    <div className="space-y-2">
                      <div
                        onClick={() => navigate("/expenses")}
                        className="p-2.5 rounded-lg bg-gradient-to-r from-rose-50 to-rose-100/50 dark:from-rose-950/40 dark:to-rose-950/20 border border-rose-200/50 dark:border-rose-700/30 flex items-center justify-between cursor-pointer hover:shadow-sm transition"
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-rose-500 text-white rounded-md text-xs">
                            <FaListAlt />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 dark:text-white">
                              School Expenditure Register
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">
                              Vendor bills & expense log
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-extrabold text-rose-600 dark:text-rose-400">
                          View →
                        </span>
                      </div>

                      <div
                        onClick={() => navigate("/my/fees/dashboard")}
                        className="p-2.5 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100/50 dark:from-emerald-950/40 dark:to-emerald-950/20 border border-emerald-200/50 dark:border-emerald-700/30 flex items-center justify-between cursor-pointer hover:shadow-sm transition"
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-emerald-500 text-white rounded-md text-xs">
                            <FaWallet />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 dark:text-white">
                              Fee Collection Dashboard
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">
                              Real-time revenue & dues
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400">
                          View →
                        </span>
                      </div>

                      <div
                        onClick={openCreateModal}
                        className="p-2.5 rounded-lg bg-gradient-to-r from-indigo-50 to-indigo-100/50 dark:from-indigo-950/40 dark:to-indigo-950/20 border border-indigo-200/50 dark:border-indigo-700/30 flex items-center justify-between cursor-pointer hover:shadow-sm transition"
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-indigo-500 text-white rounded-md text-xs">
                            <FaPlus />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 dark:text-white">
                              Add Expense Voucher
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">
                              Record new bill entry
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-extrabold text-indigo-600 dark:text-indigo-400">
                          Create →
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400 flex justify-between items-center font-medium">
                    <span>Audit Status: Verified</span>
                    <span>Updated Live</span>
                  </div>
                </motion.div>
              </div>

              {/* ════════ PAYMENT METHODS BREAKDOWN ════════ */}
              {dashboardData.paymentMethodBreakdown &&
                dashboardData.paymentMethodBreakdown.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.38, duration: 0.4 }}
                    className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md"
                  >
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <FaCreditCard className="text-indigo-500 text-xs" />
                      Payment Methods Breakdown
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                      {dashboardData.paymentMethodBreakdown.map((pm, idx) => (
                        <motion.div
                          key={idx}
                          whileHover={{ scale: 1.01, y: -1 }}
                          className="p-2.5 rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30 border border-slate-200/60 dark:border-slate-700/50"
                        >
                          <div className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            {pm.method}
                          </div>
                          <div className="text-sm md:text-base font-extrabold text-slate-900 dark:text-white mt-0.5">
                            {formatCurrency(pm.amount)}
                          </div>
                          <div className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 mt-0.5">
                            {pm.count} payments
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

              {/* ════════ TRANSACTIONS TABLE ════════ */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-md overflow-hidden"
              >
                <div className="p-3 md:p-3.5 border-b border-slate-200/50 dark:border-slate-800/50 flex flex-col md:flex-row justify-between items-start md:items-center gap-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-red-500 to-red-600 text-white shadow-sm shadow-red-500/20">
                      <FaReceipt className="text-sm" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                        Recent Transactions
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                        {filteredTransactions.length} records found
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                    <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800/50 px-2.5 py-1.5 rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                      <FaFilter className="text-slate-400 text-[10px]" />
                      <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="bg-transparent text-xs font-bold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value="ALL">All Categories</option>
                        {(dashboardData?.allCategories &&
                        dashboardData.allCategories.length > 0
                          ? dashboardData.allCategories
                          : (dashboardData?.categoryBreakdown || []).map(
                              (c) => c.name
                            )
                        ).map((catName, idx) => (
                          <option key={idx} value={catName}>
                            {catName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800/50 px-2.5 py-1.5 rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                      <FaSlidersH className="text-slate-400 text-[10px]" />
                      <select
                        value={paymentFilter}
                        onChange={(e) => setPaymentFilter(e.target.value)}
                        className="bg-transparent text-xs font-bold text-slate-900 dark:text-white outline-none cursor-pointer"
                      >
                        <option value="ALL">All Payment Modes</option>
                        {(dashboardData?.allPaymentMethods &&
                        dashboardData.allPaymentMethods.length > 0
                          ? dashboardData.allPaymentMethods
                          : [
                              "Cash",
                              "UPI",
                              "Bank Transfer",
                              "Cheque",
                              "Credit Card",
                            ]
                        ).map((pmName, idx) => (
                          <option key={idx} value={pmName}>
                            {pmName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800/50 px-2.5 py-1.5 rounded-lg border border-slate-200/50 dark:border-slate-700/50 w-full sm:w-auto min-w-[160px]">
                      <FaSearch className="text-slate-400 text-[10px]" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-transparent text-xs text-slate-900 dark:text-white outline-none w-full font-medium"
                      />
                    </div>
                  </div>
                </div>

                {filteredTransactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200/50 dark:divide-slate-800/50">
                      <thead>
                        <tr className="bg-slate-50/60 dark:bg-slate-800/40">
                          <th className="px-3.5 py-2.5 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                            <button
                              onClick={() =>
                                setSortConfig({
                                  key: "date",
                                  direction:
                                    sortConfig.key === "date" &&
                                    sortConfig.direction === "asc"
                                      ? "desc"
                                      : "asc",
                                })
                              }
                              className="flex items-center gap-1 hover:text-slate-900 dark:hover:text-white"
                            >
                              Date
                              {sortConfig.key === "date" && (
                                <span className="text-red-500 font-black">
                                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="px-3.5 py-2.5 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                            Title & Details
                          </th>
                          <th className="px-3.5 py-2.5 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                            Category
                          </th>
                          <th className="px-3.5 py-2.5 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                            Payment
                          </th>
                          <th className="px-3.5 py-2.5 text-right text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                            <button
                              onClick={() =>
                                setSortConfig({
                                  key: "amount",
                                  direction:
                                    sortConfig.key === "amount" &&
                                    sortConfig.direction === "asc"
                                      ? "desc"
                                      : "asc",
                                })
                              }
                              className="flex items-center gap-1 hover:text-slate-900 dark:hover:text-white justify-end w-full"
                            >
                              Amount
                              {sortConfig.key === "amount" && (
                                <span className="text-red-500 font-black">
                                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="px-3.5 py-2.5 text-center text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200/50 dark:divide-slate-800/50">
                        {filteredTransactions.map((tx, index) => (
                          <motion.tr
                            key={tx.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              delay: index * 0.02,
                              duration: 0.15,
                            }}
                            className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors group"
                          >
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              <div className="text-xs font-bold text-slate-900 dark:text-white">
                                {formatDate(tx.date)}
                              </div>
                            </td>
                            <td className="px-3.5 py-2.5">
                              <div className="font-bold text-slate-900 dark:text-white text-xs">
                                {tx.title}
                              </div>
                              {tx.referenceOrReceiptNo && (
                                <div className="text-[10px] font-mono text-slate-400 dark:text-slate-500">
                                  #{tx.referenceOrReceiptNo}
                                </div>
                              )}
                            </td>
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              <span className="px-2 py-0.5 inline-flex text-[11px] font-bold rounded-md bg-gradient-to-r from-red-50 to-red-100 dark:from-red-500/10 dark:to-red-500/20 text-red-700 dark:text-red-300 border border-red-200/50 dark:border-red-500/20">
                                {typeof tx.category === "object"
                                  ? tx.category?.name || "General"
                                  : tx.category || "General"}
                              </span>
                            </td>
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              <span className="px-2 py-0.5 inline-flex text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                {tx.paymentMethod || "Cash"}
                              </span>
                            </td>
                            <td className="px-3.5 py-2.5 whitespace-nowrap text-right">
                              <span className="font-extrabold text-xs text-red-600 dark:text-red-400">
                                {formatCurrency(tx.amount)}
                              </span>
                            </td>
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => openViewModal(tx)}
                                  className="p-1 text-slate-600 bg-slate-100 dark:bg-slate-800 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition cursor-pointer"
                                  title="View Details"
                                >
                                  <FaEye size={11} />
                                </button>
                                <button
                                  onClick={() => openEditModal(tx)}
                                  className="p-1 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-md hover:bg-indigo-100 transition cursor-pointer"
                                  title="Edit"
                                >
                                  <FaEdit size={11} />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteExpense(tx.id, tx.title)
                                  }
                                  className="p-1 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-md hover:bg-rose-100 transition cursor-pointer"
                                  title="Delete"
                                >
                                  <FaTrash size={11} />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <div className="text-3xl text-slate-300 dark:text-slate-700 mb-2">
                      📭
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      No transactions match your filters
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setCategoryFilter("ALL");
                        setPaymentFilter("ALL");
                      }}
                      className="mt-2 text-xs font-bold text-red-500 hover:text-red-600 transition"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* ════════ MODALS ════════ */}
      <AnimatePresence>
        {isFormModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative"
            >
              <ExpenseForm
                expense={selectedExpense}
                onClose={() => setIsFormModalOpen(false)}
                onSuccess={() => {
                  setIsFormModalOpen(false);
                  fetchDashboardData();
                }}
              />
            </motion.div>
          </motion.div>
        )}

        {isViewModalOpen && viewingExpense && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full p-6 border border-slate-200 dark:border-slate-800 shadow-2xl space-y-5"
            >
              <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25">
                    <FaReceipt size={18} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      Voucher Details
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Expense transaction receipt
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  <FaTimes size={16} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30 p-4 rounded-xl flex justify-between items-center">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Amount
                    </span>
                    <div className="text-2xl font-black text-red-600 dark:text-red-400">
                      {formatCurrency(viewingExpense.amount)}
                    </div>
                  </div>
                  <span className="px-3 py-1.5 rounded-full font-bold text-[11px] bg-gradient-to-r from-red-50 to-red-100 dark:from-red-500/10 dark:to-red-500/20 text-red-700 dark:text-red-300 border border-red-200/50 dark:border-red-500/20">
                    {typeof viewingExpense.category === "object"
                      ? viewingExpense.category?.name
                      : viewingExpense.category || "General"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Voucher
                    </span>
                    <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                      {viewingExpense.title}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Date
                    </span>
                    <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                      {formatDate(viewingExpense.date)}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Payment Mode
                    </span>
                    <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                      {viewingExpense.paymentMethod || "Cash"}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Reference No
                    </span>
                    <div className="font-mono font-bold text-slate-900 dark:text-white mt-0.5">
                      #{viewingExpense.referenceOrReceiptNo || "N/A"}
                    </div>
                  </div>
                </div>

                {viewingExpense.description && (
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">
                      Notes
                    </span>
                    <div className="text-sm text-slate-700 dark:text-slate-300">
                      {viewingExpense.description}
                    </div>
                  </div>
                )}

                {viewingExpense.attachmentUrl && (
                  <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-2">
                      Attachment
                    </span>
                    {viewingExpense.attachmentUrl.endsWith(".pdf") ? (
                      <a
                        href={viewingExpense.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-950/50 dark:to-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-xl font-bold text-sm hover:shadow-md transition"
                      >
                        <FaPaperclip /> View PDF Document
                      </a>
                    ) : (
                      <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 max-h-48">
                        <img
                          src={viewingExpense.attachmentUrl}
                          alt="Voucher"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-6 py-2.5 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl font-bold text-sm shadow-md transition cursor-pointer"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
